/**
 * QorTrace · Landing — Iter237
 *
 * Block 4 — Asymmetric **terminal vs certificate** split. Left pane is
 * a pseudo-terminal printing `cat methodology.json`, right pane is a
 * structured Audit Certificate visual with NIST FIPS posture.
 *
 * Replaces the two identical "Wiki / Compliance" cards.
 */
import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ShieldCheck } from "lucide-react";


const METHOD_JSON = `{
  "name":     "QorTrace Method",
  "version":  "v0.1",
  "auditable": true,
  "factors": [
    { "id": "wallet_format",       "weight": 18 },
    { "id": "signature_scheme",    "weight": 24 },
    { "id": "key_rotation",        "weight": 16 },
    { "id": "contract_primitives", "weight": 26 },
    { "id": "operational_hygiene", "weight": 16 }
  ],
  "stamps":   ["NIST FIPS 203", "FIPS 204", "FIPS 205"],
  "doc_count": 39
}`;


export default function TerminalFooterBlock() {
  return (
    <div
      data-testid="terminal-footer-block"
      className="grid grid-cols-1 md:grid-cols-2"
      style={{
        background: "#040914",
        border: "1px solid rgba(255,255,255,0.10)",
        borderRadius: 10,
        overflow: "hidden",
      }}
    >
      {/* ────────── Left: pseudo-terminal — Methodology + Wiki ────────── */}
      <Link
        to="/methodology"
        data-testid="terminal-methodology-cta"
        className="block group relative"
        style={{
          padding: "26px 30px 30px",
          fontFamily:
            'JetBrains Mono, ui-monospace, SFMono-Regular, monospace',
          fontSize: 13,
          color: "#D1D5DB",
          textDecoration: "none",
          borderRight: "1px solid rgba(255,255,255,0.10)",
          minHeight: 320,
          transition: "background 240ms ease",
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.background = "rgba(0,229,255,0.03)";
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.background = "transparent";
        }}
      >
        {/* Mac-style window dots — adds the "this is a terminal" cue. */}
        <div className="flex gap-2 mb-5 opacity-60">
          <span style={{ width: 11, height: 11, borderRadius: "50%", background: "#FF6E6E" }} />
          <span style={{ width: 11, height: 11, borderRadius: "50%", background: "#FFB000" }} />
          <span style={{ width: 11, height: 11, borderRadius: "50%", background: "#1FDD83" }} />
          <span
            className="ml-auto"
            style={{
              fontSize: 10, letterSpacing: "0.16em",
              color: "var(--qt-text-3)",
            }}
          >
            ~/qortrace · methodology
          </span>
        </div>

        <div style={{ color: "#1FDD83", fontSize: 12 }}>
          <span style={{ color: "var(--qt-text-3)" }}>$</span>{" "}
          cat methodology.json
        </div>
        <pre
          style={{
            color: "#D1D5DB", marginTop: 8, marginBottom: 14,
            whiteSpace: "pre", lineHeight: 1.55,
            fontSize: 12.5,
          }}
        >
{METHOD_JSON}
        </pre>
        <div style={{ color: "#1FDD83", fontSize: 12 }}>
          <span style={{ color: "var(--qt-text-3)" }}>$</span>{" "}
          ls docs/ | wc -l{" "}
          <span style={{ color: "#fff" }}>39</span>
          <span
            aria-hidden="true"
            style={{
              display: "inline-block", width: 7, height: 14,
              background: "#1FDD83", marginLeft: 6,
              verticalAlign: "middle",
              animation: "qt-blink 1.1s steps(1) infinite",
            }}
          />
        </div>
        <style>{`
          @keyframes qt-blink {
            50% { opacity: 0; }
          }
        `}</style>

        <div
          className="qt-mono mt-6 inline-flex items-center gap-2"
          style={{
            color: "#00E5FF", fontSize: 11, letterSpacing: "0.18em",
            textTransform: "uppercase", fontWeight: 700,
          }}
        >
          Read the methodology <ArrowRight size={12} />
        </div>
      </Link>

      {/* ────────── Right: Audit Certificate — Compliance / Trust ────────── */}
      <Link
        to="/trust"
        data-testid="terminal-trust-cta"
        className="block group relative"
        style={{
          padding: "30px 32px",
          background: "#0B1324",
          textDecoration: "none",
          minHeight: 320,
        }}
      >
        {/* Stamp watermark — sits at top-right corner. */}
        <svg
          aria-hidden="true"
          width="120" height="120" viewBox="0 0 120 120"
          style={{
            position: "absolute", top: 16, right: 16,
            opacity: 0.16, transform: "rotate(12deg)",
            color: "#00E5FF",
          }}
        >
          <circle cx="60" cy="60" r="56" fill="none" stroke="currentColor" strokeWidth="2"/>
          <circle cx="60" cy="60" r="44" fill="none" stroke="currentColor" strokeWidth="1"/>
          <text
            x="60" y="56" textAnchor="middle"
            fontFamily="JetBrains Mono, monospace" fontWeight="700"
            fontSize="11" letterSpacing="2" fill="currentColor"
          >
            QORTRACE
          </text>
          <text
            x="60" y="74" textAnchor="middle"
            fontFamily="JetBrains Mono, monospace" fontWeight="700"
            fontSize="9" letterSpacing="3" fill="currentColor"
          >
            VERIFIED
          </text>
        </svg>

        <div
          className="qt-mono"
          style={{
            fontSize: 10, letterSpacing: "0.20em",
            color: "#1FDD83", fontWeight: 700, textTransform: "uppercase",
          }}
        >
          CERTIFICATE OF POSTURE
        </div>
        <h3
          className="qt-h2"
          style={{
            fontSize: 24, lineHeight: 1.2,
            color: "#fff", marginTop: 8, maxWidth: "72%",
          }}
        >
          Compliance, in the open.
        </h3>
        <p
          className="text-sm"
          style={{
            color: "var(--qt-text-2)", marginTop: 10,
            lineHeight: 1.6, maxWidth: "85%",
          }}
        >
          Live posture across SOC 2, ISO 27001, NIST CSF 2.0, DORA, and
          FFIEC, plus every published policy.
        </p>

        {/* Data table — gives the pane an "auditor binder" texture. */}
        <table
          className="qt-mono w-full mt-6"
          style={{
            fontSize: 11.5, letterSpacing: "0.06em",
            color: "var(--qt-text-2)",
          }}
        >
          <tbody>
            {[
              ["STD",     "SOC 2 · ISO 27001 · NIST CSF 2.0"],
              ["PQC",     "FIPS 203 · 204 · 205"],
              ["UPTIME",  "99.97% · last 90 days"],
              ["LICENSE", "Methodology · open · versioned"],
            ].map(([k, v]) => (
              <tr
                key={k}
                style={{
                  borderBottom: "1px dashed rgba(255,255,255,0.08)",
                }}
              >
                <td
                  style={{
                    padding: "6px 0",
                    color: "var(--qt-text-3)",
                    width: 80, fontWeight: 700,
                  }}
                >
                  {k}
                </td>
                <td
                  style={{
                    padding: "6px 0",
                    color: "#fff",
                  }}
                >
                  {v}
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        <div
          className="qt-mono mt-6 inline-flex items-center gap-2"
          style={{
            color: "#1FDD83", fontSize: 11, letterSpacing: "0.18em",
            textTransform: "uppercase", fontWeight: 700,
          }}
        >
          <ShieldCheck size={12} /> View Trust Center{" "}
          <ArrowRight size={12} />
        </div>
      </Link>
    </div>
  );
}
